import {
  type FieldValues,
  useController,
  type UseControllerProps,
} from 'react-hook-form';
import { DatePicker } from '@mantine/dates';
import type { DatePickerProps } from '@mantine/dates';
import { addYears, subYears } from 'date-fns';
import { dateToString } from '../../../helpers/dateToString';

export type DatePickerFiledProps<T extends FieldValues> =
  UseControllerProps<T> &
    Omit<DatePickerProps, 'value' | 'defaultValue'> & {
      /**
       * Minimum year delta. E.g., if set to 10, the minimum year will be the current year - 10.
       */
      minYearDelta?: number;

      /**
       * Maximum year delta. E.g., if set to 10, the maximum year will be the current year + 10.
       */
      maxYearDelta?: number;

      /**
       * When set to true, the value will be returned as a string in 'yyyy-MM-dd' format.
       */
      onlyDate?: boolean;
    };

/**
 * DatePickerField is a wrapper around DatePicker component from Mantine library.
 * It is used to create a date picker input field for react-hook-form library.
 *
 * @example
 * ```tsx
 * <Form>
 *   <DatePickerField name="date" />
 * </Form>
 * ```
 *
 * @param name - name of the field
 * @param control - control object from react-hook-form
 * @param defaultValue - default value of the field
 * @param rules - validation rules for the field
 * @param shouldUnregister - should field be unregistered
 * @param onChange - onChange event handler
 * @param minYearDelta - minimum year delta. E.g. if set to 10, the minimum year will be current year - 10
 * @param maxYearDelta - maximum year delta. E.g. if set to 10, the maximum year will be current year + 10
 * @param onlyDate - when set to true, the value will be returned as a string in 'yyyy-MM-dd' format
 * @param props - other DatePicker props
 */
export function DatePickerField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  minYearDelta,
  maxYearDelta,
  onlyDate,
  ...props
}: DatePickerFiledProps<T>) {
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const minDate =
    minYearDelta !== undefined && minYearDelta !== null
      ? subYears(new Date(), minYearDelta)
      : undefined;

  const maxDate =
    maxYearDelta !== undefined && maxYearDelta !== null
      ? addYears(new Date(), maxYearDelta)
      : undefined;

  const parseDate = (data: any) => {
    if (data instanceof Date) {
      return data;
    }

    if (typeof data === 'string') {
      if (data !== '') {
        return new Date(data);
      }
      return undefined;
    }

    return data;
  };

  // @ts-ignore
  const dateValue = parseDate(value);

  return (
    <DatePicker
      withinPortal
      error={fieldState.error?.message}
      value={dateValue}
      onChange={e => {
        const newDate = onlyDate ? dateToString(e) : e;
        fieldOnChange(newDate);
        onChange?.(newDate as any);
      }}
      minDate={minDate}
      maxDate={maxDate}
      {...field}
      {...props}
    />
  );
}
